import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  UsersIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  BellIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const AnalyticsHub = ({ stats }) => {
  const navigate = useNavigate();

  const analyticsTiles = [
    {
      title: 'User Analytics',
      description: 'User growth, location breakdown, engagement metrics',
      icon: UsersIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      hoverColor: 'hover:bg-blue-100',
      features: [
        'User Growth Trends',
        'Location Analytics',
        'User Type Distribution',
        'Contact Health Metrics'
      ],
      route: '/user-analytics',
      priority: 'high'
    },
    {
      title: 'Reservation Analytics',
      description: 'Booking patterns, space utilization, reservation status',
      icon: CalendarIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      hoverColor: 'hover:bg-green-100',
      features: [
        'Reservation Trends',
        'Space Utilization',
        'Status Distribution',
        'Booking Patterns'
      ],
      route: '/reservation-analytics',
      priority: 'high'
    },
    {
      title: 'Revenue Analytics',
      description: 'Financial performance, earnings trends, payment analysis',
      icon: CurrencyDollarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      hoverColor: 'hover:bg-purple-100',
      features: [
        'Revenue Trends',
        'Payment Analysis',
        'Earnings Metrics',
        'Financial Performance'
      ],
      route: '/revenue-analytics',
      priority: 'high'
    },
    {
      title: 'Sales Person Analytics',
      description: 'Team performance, lead conversion, territory analysis',
      icon: UserGroupIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      hoverColor: 'hover:bg-orange-100',
      features: [
        'Team Performance',
        'Lead Conversion',
        'Territory Analysis',
        'Sales Metrics'
      ],
      route: '/sales-analytics',
      priority: 'medium'
    }
  ];

  const quickStats = [
    {
      title: 'Total Users',
      value: stats?.parkingUsers?.toLocaleString() || '0',
      change: '+12.5%',
      icon: UsersIcon,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Active Reservations',
      value: stats?.currentReservations?.toString() || '0',
      change: '+8.2%',
      icon: CalendarIcon,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Monthly Revenue',
      value: stats?.totalEarnings ? `₹${stats.totalEarnings.toLocaleString()}` : '₹0',
      change: '+15.3%',
      icon: CurrencyDollarIcon,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Sales Team',
      value: stats?.partners?.toString() || '0',
      change: '+2',
      icon: UserGroupIcon,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Analytics Hub</h1>
            <p className="text-gray-600 mt-2">Comprehensive insights across all business areas</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2 mt-4 sm:mt-0">
          <ChartBarIcon className="w-6 h-6 text-blue-600" />
          <span className="text-sm font-medium text-gray-600">Last updated: Just now</span>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {quickStats.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <ArrowTrendingUpIcon className="w-4 h-4 text-green-600 mr-1" />
                  <span className="text-sm font-medium text-green-600">{stat.change}</span>
                </div>
              </div>
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Analytics Categories */}
      <div className="space-y-6">
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="w-5 h-5 text-gray-600" />
          <h2 className="text-xl font-semibold text-gray-800">Analytics Categories</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {analyticsTiles.map((tile, index) => (
            <div
              key={index}
              className={`bg-white rounded-xl shadow-sm border ${tile.borderColor} p-6 transition-all duration-300 cursor-pointer group ${tile.hoverColor} hover:shadow-lg hover:scale-105`}
              onClick={() => navigate(tile.route)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-lg ${tile.bgColor}`}>
                  <tile.icon className={`w-8 h-8 ${tile.color}`} />
                </div>
                <div className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{tile.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{tile.description}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Key Features:</p>
                  <ul className="space-y-1">
                    {tile.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                        <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-2"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-3">
                  <button className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${tile.color} ${tile.bgColor} hover:${tile.hoverColor} transition-colors duration-200`}>
                    View Analytics
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <BellIcon className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-800">Recent Analytics Activity</h3>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
            <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
            <span className="text-sm text-gray-700">User Analytics updated with new growth metrics</span>
            <span className="text-xs text-gray-500 ml-auto">2 min ago</span>
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
            <div className="w-2 h-2 bg-green-600 rounded-full"></div>
            <span className="text-sm text-gray-700">Reservation trends show 15% increase in bookings</span>
            <span className="text-xs text-gray-500 ml-auto">5 min ago</span>
          </div>
          
          <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
            <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
            <span className="text-sm text-gray-700">Revenue analytics processed for Q4 2024</span>
            <span className="text-xs text-gray-500 ml-auto">10 min ago</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Need Custom Analytics?</h3>
            <p className="text-blue-100">Get insights tailored to your specific business needs</p>
          </div>
          <button className="mt-4 sm:mt-0 bg-white text-blue-600 px-6 py-2 rounded-lg font-medium hover:bg-blue-50 transition-colors duration-200">
            Request Custom Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsHub; 